import { useEffect, useRef, useState } from "react";

import { supabase } from "@/shared/lib/supabase";

const DEFAULT_TIMEOUT_MS = 120_000;
const POLL_INTERVAL_MS = 3_000;
const COMPLETED_STATUSES = ["completed", "failed", "partial"];

interface UseDocumentProcessingWatcherOptions {
  documentIds: number[];
  onProcessed: () => void;
  onTimeout?: () => void;
  timeoutMs?: number;
}

async function checkExtractionComplete(documentIds: number[]): Promise<boolean> {
  const { data, error } = await supabase
    .from("estate_documents")
    .select("id, extraction_status")
    .in("id", documentIds);

  if (error || !data) return false;
  return data.every((doc) => COMPLETED_STATUSES.includes(doc.extraction_status));
}

export function useDocumentProcessingWatcher({
  documentIds,
  onProcessed,
  onTimeout,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseDocumentProcessingWatcherOptions): { isWatching: boolean } {
  const [isWatching, setIsWatching] = useState(false);
  const onProcessedRef = useRef(onProcessed);
  const onTimeoutRef = useRef(onTimeout);
  const firedRef = useRef(false);

  useEffect(() => {
    onProcessedRef.current = onProcessed;
    onTimeoutRef.current = onTimeout;
  });

  useEffect(() => {
    if (documentIds.length === 0) return;

    firedRef.current = false;
    setIsWatching(true);

    const done = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      setIsWatching(false);
      onProcessedRef.current();
    };

    const broadcastCompleted = new Set<number>();

    const channels = documentIds.map((docId) =>
      supabase
        .channel(`doc-processed-${docId}`)
        .on("broadcast", { event: "done" }, () => {
          broadcastCompleted.add(docId);
          if (broadcastCompleted.size >= documentIds.length) done();
        })
        .subscribe(),
    );

    const poll = async () => {
      if (firedRef.current) return;
      const complete = await checkExtractionComplete(documentIds);
      if (complete) done();
    };

    poll();
    const pollInterval = setInterval(poll, POLL_INTERVAL_MS);

    const timeout = setTimeout(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      setIsWatching(false);
      if (onTimeoutRef.current) {
        onTimeoutRef.current();
      } else {
        onProcessedRef.current();
      }
    }, timeoutMs);

    return () => {
      channels.forEach((ch) => supabase.removeChannel(ch));
      clearInterval(pollInterval);
      clearTimeout(timeout);
      setIsWatching(false);
    };
  }, [documentIds, timeoutMs]);

  return { isWatching };
}
